import { NgFor, NgIf } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { ModalButton, ModalDialogOptions } from '@uzurv/rideWss/app/services';
import { isNullOrUndefined } from '@uzurv/rideWss/app/util';

@Component({
  selector: 'app-alert-modal',
  standalone: true,
  imports: [NgIf, NgFor, MatButtonModule, MatIconModule, MatDialogModule, TranslateModule],
  template: `
    <header [class.center]="data.alignTextCenter">
      <h2 *ngIf="data.title" id="alert-heading" cdkFocusInitial tabindex="-1" mat-dialog-title>
        <mat-icon *ngIf="data.icon" [svgIcon]="data.icon" id="alert-icon" aria-hidden="true"></mat-icon>{{ data.title }}
      </h2>
    </header>

    <section id="alert-section-alertMessages" mat-dialog-content [class.center]="data.alignTextCenter">
      <p *ngIf="data.message">{{ data.message }}</p>
      <p *ngIf="data.htmlContent" [innerHTML]="data.htmlContent"></p>
    </section>

    <section mat-dialog-actions align="end">
      <ng-container *ngIf="data.buttons?.length; else defaultButton">
        <ng-container *ngFor="let button of data.buttons; let i = index">
          <button
            [id]="'alert-button-' + i"
            [attr.cdkFocusInitial]="!data.title && i === 0 ? '' : null"
            [class.alert-btn-outline]="button.fill === 'outline'"
            mat-flat-button
            [color]="button.color ?? 'primary'"
            (click)="handleButtonClicked(button)"
          >
            {{ button.text }}
          </button>
        </ng-container>
      </ng-container>

      <ng-template #defaultButton>
        <button id="alert-button-default" cdkFocusInitial mat-flat-button color="primary" (click)="dismiss('confirm')">
          {{ 'button.ok' | translate }}
        </button>
      </ng-template>
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
        padding: 1.5rem;
        box-sizing: border-box;
      }

      :host [mat-dialog-actions] {
        display: flex;
        gap: 0.5rem;
        padding-top: 1rem;
      }

      :host button.alert-btn-outline {
        background-color: transparent;
        border: 1px solid var(--rss-primary-color);
        color: var(--rss-primary-color);
      }

      header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding-bottom: 1rem;
      }

      header.center {
        justify-content: center;
        text-align: center;
      }

      [mat-dialog-content].center {
        text-align: center;
      }

      h2 {
        width: 100%;
        display: flex;
        gap: 0.5rem;
        align-items: center;

        margin: 0;
        padding: 0;

        mat-icon {
          height: 2rem;
          width: 2rem;
        }
      }

      [mat-dialog-content] {
        margin: 0;
        padding: 0;
      }
    `
  ]
})
export class AlertModal {
  readonly data: ModalDialogOptions = inject(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<AlertModal, 'confirm' | 'close'>);

  async handleButtonClicked(button: ModalButton): Promise<void> {
    if (!isNullOrUndefined(button.handler)) {
      button.handler.apply(true);
    }
    await this.dismiss(button?.role);
  }

  async dismiss(role?: string): Promise<void> {
    this.dialogRef.close(role === 'confirm' ? 'confirm' : 'close');
  }
}
